import {
  Button,
  Flex,
  IconAddLine,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconTrashLine,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { memo, useCallback, useRef, useState } from "react";
import generateUuid from "../../utils/Uuid";
import { CANVAS } from "../Assessment/Plan/utils";
import { Card } from "../Card";
import CustomRTE, { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import MeasureCard from "./MeasureCard";

const TemplateSectionCard = memo(
  ({ index, lastSection, section, setSections }) => {
    const { t } = useTranslation();
    const defaultDesc = useRef(section.description);
    const [runningIndex, setRunningIndex] = useState(section.measures.length);

    const moveSectionUp = () => {
      setSections((prevSections) =>
        prevSections.map((prevSection, i) =>
          i === index - 1
            ? section
            : i === index
              ? prevSections[index - 1]
              : prevSection
        )
      );
    };

    const moveSectionDown = () => {
      setSections((prevSections) =>
        prevSections.map((prevSection, i) =>
          i === index + 1
            ? section
            : i === index
              ? prevSections[index + 1]
              : prevSection
        )
      );
    };

    const deleteSection = () => {
      setSections((prevSections) => prevSections.filter((_, i) => i !== index));
    };

    const handleHeadingChange = (e) => {
      setSections((prevSections) =>
        prevSections.map((prevSection, i) =>
          i === index ? { ...prevSection, name: e.target.value } : prevSection
        )
      );
    };

    const handleDescriptionChange = useCallback(
      (value) => {
        setSections((prevSections) =>
          prevSections.map((prevSection, i) =>
            i === index ? { ...prevSection, description: value } : prevSection
          )
        );
      },
      [index, setSections]
    );

    const handleAddMeasure = () => {
      setSections((prevSections) =>
        prevSections.map((prevSection, i) =>
          i === index
            ? {
                ...prevSection,
                measures: [
                  ...prevSection.measures,
                  {
                    index: runningIndex,
                    type: CANVAS,
                    collecting_evidence: false,
                    description_key: generateUuid(),
                    description_text: EMPTY_RTE,
                  },
                ],
              }
            : prevSection
        )
      );
      setRunningIndex(runningIndex + 1);
    };

    return (
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="100%">
          <TextInput
            id={`section-heading-section-${section.index}`}
            renderLabel={
              <Flex gap="small">
                <Flex.Item shouldGrow>
                  {t("assessment.sections.sectionHeading")}
                </Flex.Item>
                <Flex.Item>
                  <IconButton
                    color="primary-inverse"
                    screenReaderLabel={t("assessment.sections.moveSectionUp")}
                    size="small"
                    renderIcon={IconArrowOpenUpLine}
                    onClick={moveSectionUp}
                    disabled={index === 0}
                  />
                </Flex.Item>
                <Flex.Item>
                  <IconButton
                    color="primary-inverse"
                    screenReaderLabel={t("assessment.sections.moveSectionDown")}
                    size="small"
                    renderIcon={IconArrowOpenDownLine}
                    onClick={moveSectionDown}
                    disabled={!!lastSection}
                  />
                </Flex.Item>
                <Flex.Item>
                  <IconButton
                    color="danger"
                    screenReaderLabel={t("assessment.sections.deleteSection")}
                    size="small"
                    renderIcon={IconTrashLine}
                    onClick={deleteSection}
                  />
                </Flex.Item>
              </Flex>
            }
            defaultValue={section.name}
            onBlur={handleHeadingChange}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <CustomRTE
            id="section-desc"
            label={
              <Text weight="bold">{t("assessment.sections.sectionDesc")}</Text>
            }
            defaultValue={defaultDesc.current}
            onChange={handleDescriptionChange}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <Text weight="bold">{t("measures.measures")}:</Text>
          <Flex wrap="wrap" gap="small" margin="small 0 0 0">
            {section.measures.map((measure, key) => (
              <Flex.Item key={measure.index} size="100%">
                <Card
                  style={{
                    color: "#0D323F",
                    backgroundColor: "#F2F8FA",
                    border: "1px solid #ccc",
                    padding: "5px 15px 15px 15px",
                  }}
                >
                  <MeasureCard
                    index={key}
                    lastMeasure={key === section.measures.length - 1}
                    measure={measure}
                    setSections={setSections}
                    sectionIndex={index}
                  />
                </Card>
              </Flex.Item>
            ))}
            <Flex.Item textAlign="end" shouldGrow>
              <Button
                color="primary"
                renderIcon={IconAddLine}
                onClick={handleAddMeasure}
              >
                {t("measures.addMeasure")}
              </Button>
            </Flex.Item>
          </Flex>
        </Flex.Item>
      </Flex>
    );
  }
);

export default TemplateSectionCard;
